import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert } from '../ui/Alert/Alert';
import { Button } from '../ui/Button/Button';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Route-level error boundary, mounted once per route in the router so one
 * screen crashing doesn't take down the whole shell. Renders a plain,
 * friendly panel — never the error's message or stack (docs/SECURITY.md
 * §11's "no stack trace ... reaches a client" applies to the frontend too,
 * not just API responses). The detail goes to the console for local
 * debugging only.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled error rendering this screen:', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className={styles.wrapper}>
        <Alert tone="danger" title="Something went wrong">
          This page hit an unexpected error. Reloading usually fixes it — if it keeps happening, let IT
          know what you were doing.
        </Alert>
        <Button variant="secondary" onClick={this.handleReload}>
          Reload page
        </Button>
      </div>
    );
  }
}
